import {
  defineStore,
  storeToRefs,
} from 'pinia';
import { ref } from 'vue';

import { accountApiConfig } from '@/config/api/account';
import { useClientApi } from '@/plugins/api/lk';
import { useIsomorphicRouter } from '@/plugins/app-context';
import { decorateApiActions } from '@/shared/lib';
import { useAuthStore } from '@/store/authStore';
import type { CityResponse } from '@/types/Cities';
import type { LabelValue } from '@/types/Type';

import type {
  AgencyProfile,
  AgencyProfileFile,
  AgentProfile,
  ChangePasswordBody,
  Maintainer,
  PatchAgentProfileBody,
} from './types';

const STAGES = {
  CHANGE_PASSWORD: 'ChangePassword',
  CHANGE_PASSWORD_SUCCESS: 'ChangePasswordSuccess',
  RESTORE_PASSWORD: 'RestorePassword',
  RESTORE_PASSWORD_SUCCESS: 'RestorePasswordSuccess',
  FIRED_CONFIRM: 'FiredConfirm',
  FIRED_SUCCESS: 'FiredSuccess',
} as const;

interface Agency {
  name: string;
  city: string;
  files: Array<AgencyProfileFile>;
  inn: string;
  ogrn: string;
  type: LabelValue;
  maintainer: Maintainer;
}

type Profile = Omit<
  Partial<AgentProfile> & { password?: string; birthDate?: string; cityOfSale?: CityResponse },
  'city_of_sale' | 'birth_date'
>;

export const useAgentProfileStore = defineStore('agentProfile', () => {
  const authStore = useAuthStore();
  const { strategy } = storeToRefs(authStore);

  const profile = ref<Profile>({
    id: undefined,
    name: undefined,
    surname: undefined,
    patronymic: undefined,
    email: undefined,
    phone: undefined,
    agency: undefined,
    password: '*'.repeat(16),
    birthDate: undefined,
    cityOfSale: undefined,
  });

  const agency = ref<Agency | undefined>(undefined);

  const stateProfile = ref<typeof STAGES[keyof typeof STAGES] | null>(null);

  async function getAccount (): Promise<void> {
    const url = accountApiConfig.profile(strategy.value);
    const {
      id,
      name,
      surname,
      patronymic,
      email,
      phone,
      agency,
      birth_date: birthDate,
      city_of_sale: cityOfSale,
    } = await useClientApi().$get<AgentProfile>(url);

    const preparedBirthDate = birthDate
      ? Intl.DateTimeFormat('ru-RU', { dateStyle: 'short' }).format(new Date(birthDate))
      : '';
    profile.value.id = id;
    profile.value.name = name;
    profile.value.surname = surname;
    profile.value.patronymic = patronymic;
    profile.value.email = email;
    profile.value.phone = phone;
    profile.value.agency = agency;
    profile.value.birthDate = preparedBirthDate;
    profile.value.cityOfSale = cityOfSale;
  }

  async function getAgencyAccount (): Promise<void> {
    const url = accountApiConfig.agencies.profileByStrategy(strategy.value);
    agency.value = await useClientApi().$get<AgencyProfile>(url);
  }

  async function updatePassword (payload: ChangePasswordBody): Promise<void> {
    const url = accountApiConfig.changePassword(strategy.value);
    await useClientApi().$patch(url, payload);
  }

  async function updateProfile (payload: PatchAgentProfileBody): Promise<void> {
    const url = accountApiConfig.profile(strategy.value);
    await useClientApi().$patch(url, payload);
    await useAuthStore().fetchUser();
  }

  async function resetPasswordByEmail (payload: { email: string }): Promise<void> {
    const url = accountApiConfig.emailReset(strategy.value);
    await useClientApi().$post(url, payload);
  }

  async function updateAgency (payload: {
    name: string;
    procuratory_files: Array<unknown>;
    company_files: Array<unknown>;
  }): Promise<void> {
    const url = accountApiConfig.agencies.profile;
    await useClientApi().$patch(url, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    await getAgencyAccount();
  }

  async function joinAgency (inn: string): Promise<void> {
    const router = useIsomorphicRouter();
    const authStore = useAuthStore();
    const { strategy } = storeToRefs(authStore);

    const url = accountApiConfig.joinAgency(strategy.value);
    await useClientApi().$post(url, {
      agencyInn: inn,
    });

    if (strategy.value === 'represes') {
      await router.push({ query: {} }).catch(() => undefined);
      await useAuthStore().logout();
    }
  }

  function setStage (stage: keyof typeof STAGES): void {
    stateProfile.value = STAGES[stage];
  }

  const decoratedNoRethrow = decorateApiActions({
    getAccount,
    getAgencyAccount,
    resetPasswordByEmail,
  }, 'agentProfile');

  const decoratedRethrow = decorateApiActions({
    updatePassword,
    updateProfile,
    updateAgency,
    joinAgency,
  }, 'agentProfile', true);

  return {
    profile,
    agency,
    stateProfile,
    ...decoratedNoRethrow,
    ...decoratedRethrow,
    setStage,
  };
});
